'use client';

import * as React from 'react';
import { useTheme } from 'next-themes';
import { Sun, Moon, Monitor } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className={cn('h-11 w-11 rounded-pill bg-[var(--surface)] animate-pulse', className)} />
    );
  }

  const cycleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  return (
    <button
      onClick={cycleTheme}
      className={cn(
        'relative h-11 w-11 rounded-pill border border-[var(--border-default)] bg-[var(--bg-primary)]',
        'flex items-center justify-center',
        'transition-all duration-200 hover:bg-[var(--surface-hover)] hover:border-[var(--primary)]/50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/40',
        className
      )}
      aria-label={`Текущая тема: ${theme}. Нажмите для переключения.`}
    >
      {theme === 'light' && <Sun className="h-5 w-5 text-[var(--warning)]" weight="bold" />}
      {theme === 'dark' && <Moon className="h-5 w-5 text-[var(--primary)]" weight="bold" />}
      {theme === 'system' && <Monitor className="h-5 w-5 text-[var(--text-secondary)]" weight="bold" />}
    </button>
  );
}

// Простой переключатель только между light/dark
export function ThemeSwitch({ className }: ThemeToggleProps) {
  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className={cn('h-8 w-16 rounded-pill bg-[var(--surface)] animate-pulse', className)} />
    );
  }

  const isDark = resolvedTheme === 'dark';

  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className={cn(
        'relative h-8 w-16 rounded-pill transition-colors duration-300',
        isDark ? 'bg-[var(--primary)]/20' : 'bg-[var(--surface)]',
        'border border-[var(--border-default)]',
        className
      )}
      aria-label={`Переключить на ${isDark ? 'светлую' : 'тёмную'} тему`}
    >
      {/* Track icons */}
      <Sun 
        className={cn(
          'absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 transition-opacity',
          isDark ? 'opacity-30' : 'opacity-100 text-[var(--warning)]'
        )} 
        weight="fill" 
      />
      <Moon 
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 transition-opacity',
          isDark ? 'opacity-100 text-[var(--primary)]' : 'opacity-30'
        )} 
        weight="fill" 
      />
      
      {/* Thumb */}
      <span
        className={cn(
          'absolute top-1 h-6 w-6 rounded-pill bg-[var(--bg-primary)] shadow-raised transition-all duration-300',
          isDark ? 'left-9' : 'left-1'
        )}
      />
    </button>
  );
}
